import {
    ChangeDetectionStrategy,
    Component,
    inject,
    model,
    computed,
    input,
    ViewEncapsulation,
    output,
} from '@angular/core';
import { ISection } from '../models/sections-dnd-editor.models';
import { FiSectionsConfigService } from '../services/fi-sections-config.service';
import { ObjectTypes } from '@fi-app/shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { FiSectionsHelperService } from '../services/fi-sections-helper.service';
import { IContentLanguageLabelModel } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared';
import { SaveChangesWarningService } from '@fi-app/shared/fi-shared-services/save-changes-warning.service';

@Component({
    selector: 'fi-sections-summary-card',
    templateUrl: './fi-sections-summary-card.component.html',
    styleUrls: ['./fi-sections-summary-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-sections-summary-card' },
    standalone: false,
})
export class FiSectionsSummaryCardComponent {
    private fiSectionsConfigService = inject(FiSectionsConfigService);
    private appTextService = inject(AppTextService);
    private fiSectionsHelperService = inject(FiSectionsHelperService);
    private saveChangesWarningService = inject(SaveChangesWarningService);

    objectType = input.required<ObjectTypes>();
    sections = model<ISection[]>([]);
    selectedLanguageId = input.required<number>();
    getNewSectionInstanceFn = input<() => ISection>();
    isAddElementsAvailable = input<boolean>(true);
    addNewSectionItem = output<void>();

    protected summaryConfig = computed(() => this.fiSectionsConfigService.getSummaryConfig(this.objectType()));
    protected editorConfig = computed(() => this.fiSectionsConfigService.getDndEditorConfig(this.objectType()));
    protected checkpointsLabel = computed(() => {
        const summaryConfig = this.summaryConfig();
        const checkpointsAmount = this.sectionItemCounter();
        return `${checkpointsAmount} ${this.appTextService.get(
            checkpointsAmount === 1 ? summaryConfig.itemsLabelSingular : summaryConfig.itemsLabelPlural,
        )}`;
    });

    protected sectionItemCounter = computed(() => {
        const summaryConfig = this.summaryConfig();
        const sections = this.sections();
        return sections.flatMap(
            (section: ISection) => section[summaryConfig.sectionItemsPropName],
        ).length;
    });

    protected sectionsLabel = computed(() => {
        const summaryConfig = this.summaryConfig();
        const sectionsAmount = this.sectionsCounter();
        return `${sectionsAmount} ${this.appTextService.get(
            sectionsAmount === 1 ? summaryConfig.sectionsLabelSingular : summaryConfig.sectionsLabelPlural,
        )}`;
    });


    protected sectionsCounter = computed(() => {
        return this.sections().length;
    })

    protected openAddSectionDialog(): void {
        const getNewSectionInstanceFn = this.getNewSectionInstanceFn();
        if (!getNewSectionInstanceFn) {
            return;
        }
        const newSection = getNewSectionInstanceFn();
        this.fiSectionsHelperService
            .openSectionEditor(this.objectType(), this.selectedLanguageId(), true, newSection)
            .onYesButtonClick((sectionNames: IContentLanguageLabelModel[]) => {
                newSection.Names = sectionNames;
                this.sections.update((sections) => [...sections, newSection]);
            })
            .afterClosed(() => {
                if (this.editorConfig().enableSaveChangesWarning !== false) {
                    this.saveChangesWarningService.closeDialogOnTransition = false;
                }
            });

        if (this.editorConfig().enableSaveChangesWarning !== false) {
            this.saveChangesWarningService.closeDialogOnTransition = true;
        }
    }
}
